// Composer 작성 페이지의 공용 레이아웃 셸 — 헤더(뒤로가기·제목·헤더 버튼) + 콘텐츠 + 하단 컨트롤 바.
//
// 실제 작성 화면(web new-composed)과 admin 작성폼 미리보기가 **같은 셸을 공유**해 비주얼이
// 구성상 동일(parity by construction)하게 한다 — 복제 시 발생하는 드리프트를 원천 차단.
// COMPOSED(Composer) 보고서 전용 — 구 보고서(도메인 전용 new-* 페이지)는 이 셸을 쓰지 않는다.
//
// 레이아웃: flex 세로 컬럼 + 콘텐츠 flex-1 → 남는 높이를 콘텐츠가 채워 하단 바를 페이지(부모 높이)
//   하단으로 밀어낸다(해상도 기반 하단 배치). 부모가 높이를 준다(web=뷰포트, admin=디바이스 프레임).
//
// 결(2026-08-02): 시안 `remote-files/safetyhub_안전관리상태보고서.html` 정렬 — 넓은 뷰 헤더는
//   페이지 바탕 위 서브헤더(바 배경 없음, 26px 제목 + 상태 pill), 하단은 콘텐츠 컬럼 폭 안에
//   떠 있는 카드(작성자 정보 + 액션). compact(바형) 뷰 헤더만 기존 소형 바 유지.
//
// 버튼 비주얼(primary/secondary·라벨 해소)은 이 셸이 단일 정의 — 호출자는 동작(getButtonProps)만
// 제공한다(web=실제 액션, admin 미리보기=슬롯 선택). 헤더/하단 바를 활성 뷰(반응형)로 해소.

import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { buttonVariantClass } from '@safetyhub/ui';
import { resolveComposeChromeItems, resolveChromeItemDisplay } from './composeChromeResolve';
import { ChevronLeftIcon } from './chromeIcons';
import {
  COMPOSE_CHROME_ACTION_LABELS,
  COMPOSE_CHROME_CONTENT_WIDTH_PX,
  COMPOSER_VIEWS,
  DEFAULT_COMPOSE_CHROME,
  DEFAULT_COMPOSE_CHROME_CONTENT_WIDTH,
  resolveComposeSubmitLabel,
} from '@safetyhub/shared';
import type {
  ComposeChromeActionId,
  ComposeChromeConfig,
  ComposeChromeItem,
  ComposeChromeSlot,
  ComposeMode,
  ComposeSubmitSituation,
} from '@safetyhub/shared';

// 색 체계(primary/secondary)와 공통 상태를 표시형(px 패딩) 무관하게 분리 — icon 모드도 같은 색.
//
// primary 색은 packages/ui `Button`에서 **그대로 빌린다**(`buttonVariantClass`) — 값을 복붙하면
// 정본이 바뀔 때 조용히 갈린다(실제로 그라데이션 전환 때 갈렸다).
// 치수(높이·패딩)는 일부러 ui를 따르지 않는다: 이 셸은 아이콘/라벨 버튼 높이를 40px로 맞추려고
// `p-[9px]`(테두리 1px 상쇄)까지 계산해 뒀고, 라벨 패딩 `px-4`는 "px-3.5는 너무 딱 맞음"이라는
// 2026-07-10 사용자 피드백으로 정해진 값이다. ui md(38px·px-3.5)로 덮으면 그 결정을 되돌린다.
const PRIMARY_COLOR = `${buttonVariantClass('primary')} disabled:opacity-50 disabled:cursor-not-allowed`;
// secondary는 아직 수렴하지 않았다 — ui secondary는 흰 배경+테두리라, 테두리가 없는 이 회색
// 채움에 그대로 적용하면 border 1px만큼 높이가 늘어 위 40px 맞춤이 깨진다(별도 과제).
const SECONDARY_COLOR =
  'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed';
// 아이콘 전용 secondary — 채움 없는 글리프가 흰 배경에서 버튼으로 안 읽히므로 테두리형(outline).
// primary(채움)와 동일 높이, ConfirmModal 취소 버튼과 같은 outline secondary 관례.
const SECONDARY_ICON_COLOR =
  'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_BASE = 'text-sm font-medium rounded-lg transition-colors';
// '넓게' 콘텐츠 폭의 반응형 분기점 — 구 작성 페이지 규약(max-w-4xl xl:max-w-6xl)의 xl과 같은 값.
const XL_VIEWPORT_QUERY = '(min-width: 1280px)';
// 라벨(텍스트 포함) 패딩 vs 아이콘 전용 정사각 패딩.
const LABEL_PAD = 'px-5 py-2.5';
const ICON_PAD = 'p-2.5';
// 테두리형 아이콘 버튼은 border 1px만큼 패딩을 줄여 primary(무테두리, 40px)와 높이 일치.
const ICON_PAD_BORDERED = 'p-[9px]';
// 아이콘+라벨(오버라이드 시) 및 compact 뷰 라벨 버튼 — LABEL_PAD(px-5)보다 좁되
// 라벨 끝이 버튼 끝에 붙지 않을 여유(px-4 — px-3.5는 너무 딱 맞음, 2026-07-10 피드백).
const ICONLABEL_PAD = 'px-4 py-2.5';
const COMPACT_LABEL_PAD = 'px-4 py-2.5';

/**
 * 내장 액션별 기본 아이콘(lucide 스타일 path, currentColor). link(커스텀)=external-link.
 * 의존성 추가 없이 인라인 SVG(기존 뒤로가기 svg와 동형).
 */
function ActionIcon({ actionId, isLink }: { actionId?: ComposeChromeActionId; isLink: boolean }) {
  const key = actionId ?? (isLink ? 'link' : 'submit');
  const path = ACTION_ICON_PATHS[key] ?? ACTION_ICON_PATHS.submit;
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {path}
    </svg>
  );
}

const ACTION_ICON_PATHS: Record<string, ReactNode> = {
  // 체크(✓)
  submit: <path d="M20 6 9 17l-5-5" />,
  // 눈(eye)
  preview: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  // 엑스(✕)
  cancel: <path d="M18 6 6 18M6 6l12 12" />,
  // 반시계 화살표(↺ rotate-ccw)
  reset: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </>
  ),
  // external-link
  link: (
    <>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </>
  ),
};

export interface ComposeChromeButtonProps {
  onClick: () => void;
  disabled?: boolean;
  /** 동적 라벨(예: 제출 중 "작성 중..."). 미지정 시 item.label 또는 액션 기본 라벨. */
  labelOverride?: string;
}

/** 편집기 슬롯 선택(admin 미리보기) — 선택 하이라이트 + 클릭 시 그 슬롯 선택. */
export interface ComposeSlotInteractive {
  selected: boolean;
  onSelect: () => void;
}

// 타이틀 fit-to-width — 좁은 뷰(바형)에서 긴 보고서명이 …로 잘리는 대신, 폭에 들어올 때까지
// 폰트를 동적으로 축소(base → 하한 13px)해 "…보고서 작성" 전체를 온전히 표시.
// 하한에서도 넘치는 극단만 기존 truncate(…) 폴백. nowrap(truncate) 전제라 scrollWidth=전체 텍스트 폭.
// base는 뷰별로 다르다: 넓은 뷰=시안 서브헤더 26px(.rep-title-row h1), compact(바형)=기존 18px.
const TITLE_BASE_PX_WIDE = 26;
const TITLE_BASE_PX_COMPACT = 18;
const TITLE_MIN_PX = 13;

function useFitTitle(text: string, basePx: number) {
  const ref = useRef<HTMLHeadingElement>(null);
  const lastWidthRef = useRef(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      lastWidthRef.current = el.clientWidth;
      let size = basePx;
      el.style.fontSize = `${size}px`;
      while (size > TITLE_MIN_PX && el.scrollWidth > el.clientWidth) {
        size -= 0.5;
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    // 컨테이너 폭 변화(리사이즈·뷰 전환) 시 재계산. 자기 유발 RO 재진입은 폭 비교로 차단.
    const ro = new ResizeObserver(() => {
      if (el.clientWidth !== lastWidthRef.current) fit();
    });
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => ro.disconnect();
  }, [text, basePx]);
  return ref;
}

// ── 시안 결(remote-files/safetyhub_안전관리상태보고서.html) ──────────────────────────────
// 서브헤더(.rep-subheader/.rep-back-row/.rep-title-row/.status-pill, CSS 425~441)와
// 푸터(.report-footer/.footer-user, CSS 867~883)의 **모양 값은 전부 인라인 style로 소유**한다 —
// rc 전용 Tailwind 유틸은 앱 CSS에 미생성될 수 있다(2026-08-02 결정 기록 `composed-write-form-mockup-style`).
const INK_900 = '#0f172a';
const INK_600 = '#475569';
const INK_500 = '#64748b';
const LINE = '#e8ecf2';
const SLATE_BG = '#f1f5f9';
// 페이지 바탕 — web Layout 본문 바탕과 동일 명시. 헤더/푸터가 배경 없이 이 바탕 위에 놓이는
// 구도라, 호스트 바탕 상속에 기대면 admin 디바이스 프레임(흰색)에서 갈라진다.
const PAGE_GROUND = '#f3f4f6';

const SUBHEADER_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 20,
  // 시안 padding 6px 0 20px + 콘텐츠 컬럼 좌우 패딩(16px = 본문 px-4와 동일 축).
  padding: '6px 16px 20px',
};
// color 없음이 의도 — 색은 부모(좌측 컬럼)에서 상속시킨다. 인라인 color를 주면
// hover:text-brand-600(양쪽 앱에 기존 사용 확인분)이 특이도에서 밀려 죽는다.
// Tailwind preflight가 button의 color:inherit를 보장한다.
const BACK_ROW_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  marginBottom: 8,
  fontSize: 13,
  fontWeight: 650,
  letterSpacing: '-.005em',
};
const TITLE_ROW_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 14,
  flexWrap: 'wrap',
  minWidth: 0,
};
const TITLE_STYLE: CSSProperties = {
  fontSize: TITLE_BASE_PX_WIDE,
  fontWeight: 800,
  color: INK_900,
  letterSpacing: '-.025em',
  lineHeight: 1.2,
  minWidth: 0,
};
const SUBTITLE_STYLE: CSSProperties = { fontSize: 14, fontWeight: 600, color: INK_500 };
const HEADER_ACTIONS_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flex: 'none',
};
const STATUS_PILL_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px 5px 8px',
  borderRadius: 999,
  fontSize: 13.5,
  fontWeight: 650,
  letterSpacing: '-.005em',
  whiteSpace: 'nowrap',
  background: SLATE_BG,
  color: INK_600,
  border: `1px solid ${LINE}`,
};
const STATUS_DOT_STYLE: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: INK_500,
  flex: 'none',
};
/** 작성 화면의 상태 표시는 항상 "작성 중"(DRAFT) — create/edit 모두 저장 전 상태다. */
const STATUS_PILL_LABEL = '작성 중';

/**
 * 떠 있는 하단 카드 결 — 시안의 모든 작성·상세류 푸터 공통 골격(.report-footer/.ir-footer/
 * .tbm-footer: radius 14 · line 테두리 · sticky 하단 · 그림자). ReportWriteShell(구 보고서
 * 상세/작성 셸)도 이 정본을 공유한다 — 각자 정의하면 두 셸의 하단 바가 조용히 갈린다.
 */
export const FOOTER_CARD_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 22px',
  borderRadius: 14,
  border: `1px solid ${LINE}`,
  background: 'rgba(255,255,255,.94)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  boxShadow: '0 -2px 6px rgba(15,23,42,.04),0 14px 26px -10px rgba(15,23,42,.2)',
};
// 시안 .footer-user(작성자 아바타·이름·역할)는 **의도적으로 채택하지 않는다** — 사이드바 하단에
// 로그인 계정 정보가 상시 표시돼 중복이고, 상세류 시안(.ir-footer)은 버튼만이라 작성/상세 간
// 일관도 깨진다(2026-08-02 사용자 결정 — 결정 기록 composed-write-form-mockup-style 참조).
// 버튼은 우측 정렬(시안 space-between에서 좌측 비운 동형).
const FOOTER_ACTIONS_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginLeft: 'auto',
};

function chromeItemLabel(item: ComposeChromeItem, situation: ComposeSubmitSituation): string {
  // submit은 상황 해소가 먼저다 — item.label은 **기본 라벨만** 대체하고(baseLabelOverride),
  // 카운터·전환 접미는 파생 층 소유라 저작이 지우지 못한다(결정 2026-09-06 §3).
  if (item.actionId === 'submit') return resolveComposeSubmitLabel(situation, item.label);
  if (item.label) return item.label;
  if (item.actionId) return COMPOSE_CHROME_ACTION_LABELS[item.actionId];
  return '버튼';
}

export function ComposeWritePageShell({
  config,
  activeView,
  mode = 'create',
  submitSituation,
  title,
  subtitle,
  getButtonProps,
  onBack,
  backLabel = '이전 페이지',
  headerInteractive,
  footerInteractive,
  showSlotAffordance = false,
  minHeight,
  stickyChrome = false,
  stickyTopOffset = 0,
  className,
  children,
}: {
  /** 템플릿 composeChrome(미지정=기본 세트). */
  config?: ComposeChromeConfig;
  /** 활성 반응형 뷰키 — 헤더/하단 버튼 뷰별 오버라이드 해소. */
  activeView: string;
  /** 작성 모드(create/edit) — 항목 visibleIn 해소 + submit 라벨. 미지정=create. */
  mode?: ComposeMode;
  /**
   * submit 버튼 상황(분할 진행·이어질 흐름 CTA) — 미지정이면 `{ mode }`, 즉 현행 모드별
   * 기본 라벨 그대로다(무회귀). 결정 2026-09-06 §3.
   *
   * `mode`는 이 객체가 아니라 **셸의 `mode` prop이 정본**이다(둘이 갈리면 visibleIn 해소와
   * 라벨이 서로 다른 모드를 보게 되므로 셸이 덮어쓴다) — 호출자는 splitProgress·
   * postSubmitCtaLabel만 채우면 된다.
   */
  submitSituation?: ComposeSubmitSituation;
  /** 헤더 대제목(예: "안전관리상태보고서 작성"). */
  title: string;
  /** 헤더 소제목(예: 현장명). 미지정 시 소제목 줄 미표시. */
  subtitle?: string;
  /** 각 버튼의 동작(호출자 제공). 비주얼은 셸이 담당. */
  getButtonProps: (item: ComposeChromeItem, slot: ComposeChromeSlot) => ComposeChromeButtonProps;
  /** 뒤로가기 동작(실화면). 미지정 시 뒤로가기는 비동작 시각 요소(미리보기). */
  onBack?: () => void;
  /**
   * 뒤로가기 목적지 라벨(chevron 옆 텍스트, 구 작성 페이지 StickyHeader 관례) — 실화면은 실제 목적지
   * (예: '점검 상세'), admin 미리보기는 목적지를 모르므로 기본 '이전 페이지'.
   */
  backLabel?: string;
  /** (admin 미리보기) 헤더 슬롯 선택/하이라이트. */
  headerInteractive?: ComposeSlotInteractive;
  /** (admin 미리보기) 하단 바 슬롯 선택/하이라이트. */
  footerInteractive?: ComposeSlotInteractive;
  /** (admin 미리보기) "헤더"/"하단 컨트롤 바" 슬롯 배지 표시. */
  showSlotAffordance?: boolean;
  /** (admin 미리보기) 디바이스 프레임 최소 높이(해상도 기반). web은 미지정(부모 뷰포트). */
  minHeight?: number | string;
  /**
   * (web 전용) 헤더 바를 뷰포트 상단에 sticky·하단 컨트롤 바를 fixed로 고정. 미지정/false=기존
   * static(흐름) — admin 캔버스(transform:scale 프레임)는 절대 켜지 않는다(fixed/sticky가 스케일
   * 박스에 갇혀 깨짐). 스크롤 컨테이너=document인 web에서만 사용.
   */
  stickyChrome?: boolean;
  /**
   * (stickyChrome 전용) sticky 헤더 바의 top 오프셋(px). 앱 헤더(모바일 sticky) 아래에 붙도록
   * 호출자가 계산해 전달(rc는 앱 레이아웃을 모르므로 web이 소유 — StickyHeader `top-14 lg:top-0`
   * 동형). 데스크톱(lg+, 앱 헤더 없음)=0.
   */
  stickyTopOffset?: number;
  /** 셸 외곽(root) 추가 클래스. */
  className?: string;
  /** 콘텐츠(web=기본정보+작성폼, admin=위젯 캔버스). */
  children: ReactNode;
}) {
  const chrome = config ?? DEFAULT_COMPOSE_CHROME;
  const { headerItems, actionBarItems } = resolveComposeChromeItems(chrome, activeView, mode);
  // mode는 셸 prop이 정본 — 상황 객체가 다른 모드를 실어 와도 라벨과 visibleIn이 갈리지 않게.
  const situation: ComposeSubmitSituation = { ...submitSituation, mode };

  // compact(좁은) 뷰 여부 — 라벨 버튼 수평 패딩 압축에 사용(버튼 폭은 라벨 hug, 늘리지 않음).
  const isCompactView =
    COMPOSER_VIEWS.find((v) => v.key === activeView)?.chromeCompact === true;

  const renderBtn = (item: ComposeChromeItem, slot: ComposeChromeSlot) => {
    const p = getButtonProps(item, slot);
    // labelOverride(제출 중 "작성 중…" 등 전이 라벨)는 종전대로 최우선 — 상황 접미 대상이 아니다.
    const label = p.labelOverride ?? chromeItemLabel(item, situation);
    const display = resolveChromeItemDisplay(item, activeView);
    const isIconOnly = display === 'icon';
    const color = item.primary
      ? PRIMARY_COLOR
      : isIconOnly
        ? SECONDARY_ICON_COLOR
        : SECONDARY_COLOR;
    // 버튼 폭 = 라벨 hug(늘리지 않음 — 2026-07-10 사용자 확정). compact 뷰는 수평 패딩만 압축.
    return (
      <button
        key={item.id}
        type="button"
        // stopPropagation: admin 미리보기의 "빈 영역 클릭=선택 해제" 컨테이너로 버블돼
        // 방금 한 슬롯 선택이 즉시 풀리는 것 방지(실화면엔 상위 핸들러 없어 무영향).
        onClick={(e) => {
          e.stopPropagation();
          p.onClick();
        }}
        disabled={p.disabled}
        className={`${BTN_BASE} ${color} ${
          isIconOnly
            ? item.primary
              ? ICON_PAD
              : ICON_PAD_BORDERED
            : display === 'iconLabel'
              ? ICONLABEL_PAD
              : isCompactView
                ? COMPACT_LABEL_PAD
                : LABEL_PAD
        } ${display === 'iconLabel' ? 'inline-flex items-center gap-1.5' : ''}`}
        // 아이콘만일 때 접근성·툴팁 라벨 필수.
        aria-label={isIconOnly ? label : undefined}
        title={isIconOnly ? label : undefined}
      >
        {display === 'label' ? (
          label
        ) : (
          <>
            <ActionIcon actionId={item.actionId} isLink={item.link != null} />
            {display === 'iconLabel' && <span>{label}</span>}
          </>
        )}
      </button>
    );
  };

  // 슬롯 바(헤더/하단) 인터랙션 — admin 선택 시 바 전체가 클릭 대상(전폭). 하이라이트는
  // purge-무관 인라인 스타일(선택=indigo-50 bg + inset ring). 실화면은 미지정 → 효과 0.
  const interactiveProps = (it?: ComposeSlotInteractive) =>
    it
      ? {
          role: 'button' as const,
          tabIndex: 0,
          // stopPropagation: 빈 영역 클릭=해제 컨테이너로의 버블 차단(슬롯 선택 유지).
          onClick: (e: { stopPropagation: () => void }) => {
            e.stopPropagation();
            it.onSelect();
          },
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              it.onSelect();
            }
          },
          className: 'cursor-pointer',
          style: it.selected
            ? { backgroundColor: '#eef2ff', boxShadow: 'inset 0 0 0 1px #818cf8' }
            : undefined,
        }
      : { className: '', style: undefined };

  const headerI = interactiveProps(headerInteractive);
  const footerI = interactiveProps(footerInteractive);
  // 콘텐츠 폭 제한(가운데 컬럼) — 바(bar)는 전폭, 그 안 내용/본문만 이 폭으로 가운데 정렬.
  // 폭은 인라인 style(px) — rc 전용 Tailwind 유틸은 앱 CSS에 미생성될 수 있고(purge/watch),
  // admin 디바이스 프레임에선 뷰포트 기반 반응형이 프레임 폭과 어긋남(메모리 rc-tailwind-purge).
  //
  // '넓게'(기본)는 **web 실폼(stickyChrome)에서만 반응형 상한** — 구 작성 페이지 규약
  // (max-w-4xl xl:max-w-6xl = 1280 미만 896 / 이상 1152)과 동일하게 좁은 창에서 896으로
  // 줄어든다(2026-08-04 사용자 제보: 1280 미만 창에서 구 페이지 대비 여백이 갈림).
  // admin(디바이스 프레임·시뮬레이션)은 종전 고정 px 유지(뷰포트 반응형이 프레임과 어긋남).
  //
  // ⚠️ 초기값을 lazy initializer로 **첫 렌더부터 실측**한다. 상수 true로 시작하면 1280 미만 창에서
  // 매 진입 시 wide(1152)로 한 프레임 그려졌다가 effect 후 normal(896)로 좁아진다 — 이 축이 고치려던
  // 바로 그 여백이 진입 순간마다 틀린 폭으로 보였다.
  // SSR 가드는 `typeof window === 'undefined'`부터 — `typeof window.matchMedia`는 `window` 식별자를
  // 먼저 평가하므로 window 자체가 없는 환경에서는 가드가 되지 못하고 ReferenceError가 난다.
  // 구독은 useLayoutEffect — 페인트 전에 붙어야 초기 측정과 구독 사이의 변화도 놓치지 않는다.
  const readIsXlViewport = (): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia(XL_VIEWPORT_QUERY).matches;
  };
  const [isXlViewport, setIsXlViewport] = useState(readIsXlViewport);
  useLayoutEffect(() => {
    if (!stickyChrome || typeof window === 'undefined' || typeof window.matchMedia !== 'function')
      return;
    const mq = window.matchMedia(XL_VIEWPORT_QUERY);
    const apply = (): void => setIsXlViewport(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [stickyChrome]);
  const effectiveContentWidth = chrome.contentWidth ?? DEFAULT_COMPOSE_CHROME_CONTENT_WIDTH;
  const contentMaxWidthPx =
    stickyChrome && effectiveContentWidth === 'wide' && !isXlViewport
      ? COMPOSE_CHROME_CONTENT_WIDTH_PX.normal
      : COMPOSE_CHROME_CONTENT_WIDTH_PX[effectiveContentWidth];
  const contentColumnStyle: CSSProperties = {
    maxWidth: contentMaxWidthPx,
    marginLeft: 'auto',
    marginRight: 'auto',
  };
  // 타이틀 fit-to-width(… 대신 폰트 축소) — 텍스트/폭 변화에 반응. base는 뷰별(넓은 26 / 바형 18).
  const titleRef = useFitTitle(
    title || '보고서 작성',
    isCompactView ? TITLE_BASE_PX_COMPACT : TITLE_BASE_PX_WIDE
  );

  // web 뷰포트 고정(stickyChrome) — 인라인 style(px)로 소유(rc 전용 Tailwind 유틸 purge 회피).
  // z-40대: 앱 헤더(z-[60])·배너(z-[55])·사이드바(z-50) 아래. admin은 stickyChrome 미전달 → 전부 미적용.
  // stickyChrome과 admin 선택 하이라이트(headerI/footerI.style)는 상호 배타(admin은 stickyChrome 미전달).
  //
  // **헤더 sticky는 compact(바형) 뷰에서만.** 시안은 "흐름에 놓인 서브헤더 + 떠 있는 푸터" 구도이고
  // 기본 chrome은 헤더 슬롯이 비어 있어(제목만) 상단 고정의 이득이 거의 없다. 반면 좁은 화면은
  // 제목/뒤로가기가 화면 밖으로 나가면 이동 비용이 커서 기존 top-14 고정 구도를 그대로 둔다.
  const stickyHeaderStyle: CSSProperties | undefined =
    stickyChrome && isCompactView
      ? { position: 'sticky', top: stickyTopOffset, zIndex: 40 }
      : headerI.style;
  // 떠 있는 카드가 하단 nav 위 16px에 뜬다(시안 .report-footer bottom:16px).
  //
  // ⚠️ 가로 축은 호스트가 소유한다 — `left`는 **호스트 콘텐츠 컬럼의 좌측 오프셋**이고
  // `--bottom-nav-height`(세로)와 같은 축이다. `position: fixed`는 뷰포트 기준이라 호스트의
  // 사이드바 들여쓰기를 스스로 알지 못하고, 그러면 안쪽 `contentColumnStyle`(maxWidth + margin auto)이
  // **뷰포트** 중앙에 정렬돼 그 폭의 절반만큼 왼쪽으로 쏠린다. web `Layout.tsx`가 그 변수를
  // 설정하고, **기본값 0이라 Layout 밖(외부 결재·인쇄)과 admin은 영향이 없다**
  // (admin은 사이드바가 가변 폭이라 여기에 상수를 박으면 반대로 틀어진다 — 그래서 CSS 변수다).
  // ⚠️ **이 정렬을 재는 자동 검사는 없다**(레이아웃은 jsdom이 못 잰다 — `getBoundingClientRect()`가
  // 0을 준다) — 바꾸면 **전후 캡처**로 확인할 것. 2026-10-13에 이 자리가 `left: 0`이라
  // 128px 왼쪽으로 쏠려 있었다(사이드바 256 ÷ 2 — 같은 셸의 헤더는 `sticky`라 흐름 안이어서 무사했다).
  const fixedFooterStyle: CSSProperties | undefined = stickyChrome
    ? {
        position: 'fixed',
        left: 'var(--app-content-left, 0px)',
        right: 0,
        bottom: 'calc(var(--bottom-nav-height, 0px) + 16px)',
        zIndex: 40,
      }
    : { backgroundColor: PAGE_GROUND, ...footerI.style };
  // fixed 하단 카드가 콘텐츠 끝을 가리지 않도록 하단 여백 확보(카드 높이 ~70px + bottom 16px + 여유).
  const contentStyle: CSSProperties = stickyChrome
    ? { ...contentColumnStyle, paddingBottom: 'calc(var(--bottom-nav-height, 0px) + 7rem)' }
    : contentColumnStyle;

  return (
    <div
      className={`flex flex-col ${className ?? ''}`}
      style={minHeight != null ? { minHeight } : undefined}
    >
      {isCompactView ? (
        /* compact(바형) 헤더 — 좁은 폭에 26px 제목이 부적합해 기존 소형 바를 유지한다.
           가장자리까지 전폭(구 StickyHeader 관례: bg+하단 경계선). */
        <div
          {...headerI}
          className={`border-b border-gray-200 bg-gray-50 ${headerI.className}`}
          style={stickyHeaderStyle}
        >
          <div className="flex w-full items-center gap-4 px-4 py-3" style={contentColumnStyle}>
            {/* chevron + 목적지 라벨. onBack 없으면(미리보기) 비동작 시각 요소. */}
            {chrome.showBack !== false &&
              (onBack ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onBack();
                  }}
                  className="flex shrink-0 items-center gap-1 text-gray-600 transition-colors hover:text-gray-900"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">{backLabel}</span>
                </button>
              ) : (
                <span className="flex shrink-0 items-center gap-1 text-gray-600">
                  <ChevronLeftIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">{backLabel}</span>
                </span>
              ))}
            {/* | 구분자 + 제목/부제목(공간|공간). 여백은 인라인(px) — rc 전용 Tailwind 유틸은
                앱 CSS에 미생성될 수 있어(purge/watch) 인라인이 정본(메모리 rc-tailwind-purge). */}
            {chrome.showTitle !== false && (
              <div
                className={`min-w-0 ${chrome.showBack !== false ? 'border-l border-gray-300' : ''}`}
                style={chrome.showBack !== false ? { marginLeft: -4, paddingLeft: 12 } : undefined}
              >
                <h1
                  ref={titleRef}
                  className="truncate text-lg font-semibold leading-tight text-gray-900"
                >
                  {title || '보고서 작성'}
                </h1>
                {subtitle && <p className="truncate text-sm text-gray-500">{subtitle}</p>}
              </div>
            )}
            {headerItems.length > 0 && (
              <div className="ml-auto flex items-center gap-2">
                {headerItems.map((it) => renderBtn(it, 'header'))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* 시안 서브헤더(.rep-subheader) — 바 배경/경계선 없이 페이지 바탕 위, 콘텐츠 컬럼 폭 안.
           바깥 래퍼는 전폭 유지(admin 슬롯 선택 클릭 대상이 바 전체라는 기존 규약). */
        <div
          {...headerI}
          className={headerI.className}
          style={{ backgroundColor: PAGE_GROUND, ...headerI.style }}
        >
          <div className="w-full" style={{ ...contentColumnStyle, ...SUBHEADER_STYLE }}>
            {/* 좌측 컬럼: back row(1층) + 제목 행(2층). color는 여기서 상속시킨다 — back row에
                인라인 color를 주면 hover:text-brand-600이 특이도에서 밀린다. */}
            <div style={{ minWidth: 0, color: INK_500 }}>
              {chrome.showBack !== false &&
                (onBack ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onBack();
                    }}
                    className="transition-colors hover:text-brand-600"
                    style={BACK_ROW_STYLE}
                  >
                    <ChevronLeftIcon className="w-3.5 h-3.5" />
                    <span>{backLabel}</span>
                  </button>
                ) : (
                  <span style={BACK_ROW_STYLE}>
                    <ChevronLeftIcon className="w-3.5 h-3.5" />
                    <span>{backLabel}</span>
                  </span>
                ))}
              {chrome.showTitle !== false && (
                <div style={TITLE_ROW_STYLE}>
                  <h1 ref={titleRef} className="truncate" style={TITLE_STYLE}>
                    {title || '보고서 작성'}
                  </h1>
                  {subtitle && <span style={SUBTITLE_STYLE}>{subtitle}</span>}
                </div>
              )}
            </div>
            {/* 우측: 상태 pill + 헤더 슬롯 버튼(저작 chrome — 기능·해소 불변). */}
            {(chrome.showTitle !== false || headerItems.length > 0) && (
              <div style={HEADER_ACTIONS_STYLE}>
                {chrome.showTitle !== false && (
                  <span style={STATUS_PILL_STYLE}>
                    <span style={STATUS_DOT_STYLE} aria-hidden />
                    {STATUS_PILL_LABEL}
                  </span>
                )}
                {headerItems.map((it) => renderBtn(it, 'header'))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 콘텐츠 — 남는 높이 채움(하단 바를 페이지 하단으로). 내용만 컬럼 폭+패딩.
          배경 #f3f4f6(gray-100) = web Layout 본문 바탕과 동일 명시 — 섹션 본문(투명)이 이 색을
          투과해야 구 SMS와 같은 결. admin 캔버스는 디바이스 프레임이 bg-white라 명시가 없으면
          투명 섹션 뒤가 흰색으로 갈라짐(2026-07-14 사용자 실측 #f3f5f7≈gray-100 대조). 인라인
          고정은 rc purge 규약 + web Layout 색 변경 시 이 값도 동기화할 것(PAGE_GROUND 단일 정의 —
          헤더/푸터도 같은 바탕을 명시 소유해 페이지 바탕이 끊기지 않는다). */}
      <div className="flex-1" style={{ backgroundColor: PAGE_GROUND }}>
        <div className="w-full px-4 py-6" style={contentStyle}>
          {children}
        </div>
      </div>

      {/* 하단 컨트롤 바 — 시안 .report-footer: 콘텐츠 컬럼 폭 안에 떠 있는 카드.
          바깥 래퍼는 전폭 유지(sticky 시 fixed 컨테이너 / admin 슬롯 선택 클릭 대상).
          sticky일 때 래퍼는 투명·무테두리이고, 위 결은 전부 안쪽 카드가 소유한다. */}
      <div {...footerI} className={footerI.className} style={fixedFooterStyle}>
        <div
          className="w-full"
          style={{ ...contentColumnStyle, padding: stickyChrome ? '0 16px' : '0 16px 24px' }}
        >
          <div style={FOOTER_CARD_STYLE}>
            <div style={FOOTER_ACTIONS_STYLE}>
              {actionBarItems.length > 0
                ? actionBarItems.map((it) => renderBtn(it, 'actionBar'))
                : showSlotAffordance && <span className="text-xs text-gray-400">버튼 없음</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
